import React from 'react';

import APIHandler from 'config/APIHandler';

import { getFancyBoxOptions } from 'helpers/getFancyBoxOptions';
import { getIframeTrackType, getResourceType } from 'helpers/getResourceType';
import { getResourceUrl } from 'helpers/getResourceUrl';

import { useImgix } from 'hooks/useImgix';

import c from './_resourceTile.module.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

export interface ResourceTileProps {
  type: string;
  image: string;
  title: string;
  subtitle?: string;
  url: string | string[];
  appId: number | string;
  uniqueId: string;
  children?: React.ReactNode;
  locked: boolean;
  lockedText?: React.ReactNode;
  className?: string | undefined;
  basePath?: string;
  zoomOnHover?: true | false;
}

export function ResourceTile(props: ResourceTileProps) {
  const { title, subtitle, image, className } = props;

  return (
    <Sentry.ErrorBoundary
      fallback={() => (
        <a className={clsx(c.tile, 'resource-tile', className)} data-testid="resource-tile">
          <div className={c.container}>
            <div className={c.text}>
              {title && <h4 className="global__text--title">{title}</h4>}
              {subtitle && <h6>{subtitle}</h6>}
            </div>
            <div className={c.imageContainer}>
              <img src={image} className={c.image} alt="" />
              <div className={c.overlay} />
            </div>
          </div>
        </a>
      )}
      beforeCapture={(scope) => {
        scope.setTag('component', 'ResourceTile');
        scope.setExtra('type', props.type);
        scope.setExtra('title', title);
        scope.setExtra('uniqueId', props.uniqueId);
        scope.setExtra('appId', props.appId);
      }}
    >
      <ResourceTileContent {...props} />
    </Sentry.ErrorBoundary>
  );
}

function ResourceTileContent({
  type,
  image,
  title,
  subtitle,
  url,
  appId,
  uniqueId,
  children,
  locked,
  lockedText,
  className,
  basePath,
  zoomOnHover = false,
}: ResourceTileProps) {
  const [storedUser] = useLocalStorage<{ rawData: { email?: string } } | null>('user', null);
  const userEmail = storedUser?.rawData?.email ?? 'Guest';

  const { resolvedUrl, shareUrl, dataSrc, linkTo } = getResourceUrl({ type, url, title, uniqueId, basePath });
  const resourceType = getResourceType({ type });

  if (resourceType === 'fallback') {
    throw new Error(`ResourceTile: unrecognized resource type "${type}" (uniqueId: ${uniqueId}, appId: ${appId})`);
  }

  const fancyBoxProps = import.meta.env.MODE === 'test' ? {} : getFancyBoxOptions({ uniqueId, shareUrl, image, title });

  const { src: imgixSrc } = useImgix(image, { imgix: { width: 384 } });

  const fire = (trackType: string) => {
    void (APIHandler.trackEvent(appId, userEmail, 'collateral', trackType, resolvedUrl, title) as Promise<unknown>);
  };

  const tileContent = (
    <React.Fragment>
      <div className={clsx(c.container, { [c.zoom]: zoomOnHover })}>
        {children ?? (
          <React.Fragment>
            <div className={c.text}>
              {title && <h4 className="global__text--title">{title}</h4>}
              {subtitle && <h6>{subtitle}</h6>}
            </div>
            <div className={c.imageContainer}>
              <img src={imgixSrc} className={c.image} alt="" />
              <div className={c.overlay} />
            </div>
          </React.Fragment>
        )}
      </div>
      {locked && lockedText && (
        <div className={c.unlock}>
          <FontAwesomeIcon icon={['fas', 'lock']} />
          <span>{lockedText}</span>
        </div>
      )}
    </React.Fragment>
  );

  if (resolvedUrl.includes('https://stack.travpromobile.com/instagram.php?embed_id=')) {
    const instagramOptions = JSON.stringify({
      infobar: true,
      thumbs: { autoStart: true, axis: 'x' },
      baseClass: 'customBox',
      buttons: ['thumbs', 'close'],
    });
    return (
      <a
        onClick={() => {
          fire('web');
        }}
        data-type="iframe"
        data-src={resolvedUrl}
        href={resolvedUrl}
        className={clsx(c.tile, 'resource-tile', className)}
        {...fancyBoxProps}
        data-options={instagramOptions}
      >
        {tileContent}
      </a>
    );
  }

  if (
    resolvedUrl.includes('travpro.yourworldapps.nl/directory/thailand_dir/map.php') ||
    resolvedUrl.includes('travpro.yourworldapps.nl/directory/thailand_dir/map-google.php')
  ) {
    return (
      <a
        onClick={() => {
          fire('map');
        }}
        data-type="iframe"
        data-src={resolvedUrl}
        href={resolvedUrl}
        className={clsx(c.tile, 'resource-tile', className)}
        {...fancyBoxProps}
      >
        {tileContent}
      </a>
    );
  }

  if (resolvedUrl.includes('thaitheknotspecialist.com/main/sell/templates-mobile')) {
    return (
      <Link
        onClick={() => {
          fire('view');
        }}
        to="/main/sell/templates"
        className={clsx(c.tile, 'resource-tile', className)}
      >
        {tileContent}
      </Link>
    );
  }

  if (locked) {
    return (
      <div className={clsx(c.tile, c.tileLocked, 'resource-tile', className)} data-testid="resource-tile">
        {tileContent}
      </div>
    );
  }

  if (resourceType === 'webLink') {
    return (
      <a
        onClick={() => {
          fire('web');
        }}
        href={resolvedUrl}
        target="_blank"
        rel="noreferrer noopener"
        className={clsx(c.tile, 'resource-tile', className)}
      >
        {tileContent}
      </a>
    );
  }

  if (resourceType === 'iframe') {
    const isIframe = type === 'iframe' || type === 'iframe-link';
    const iframeTrackType = getIframeTrackType(type);
    return (
      <div
        onClick={() => {
          fire(iframeTrackType);
        }}
        data-src={dataSrc}
        className={clsx(c.tile, 'resource-tile', className)}
        {...fancyBoxProps}
        {...(isIframe ? { 'data-type': 'iframe' } : {})}
      >
        {tileContent}
      </div>
    );
  }

  if (resourceType === 'programLink') {
    return (
      <Link
        onClick={() => {
          fire(type);
        }}
        to={linkTo}
        className={clsx(c.tile, 'resource-tile', className)}
      >
        {tileContent}
      </Link>
    );
  }

  return (
    <a
      href={resolvedUrl}
      className={clsx(c.tile, 'resource-tile', className)}
      data-testid="resource-tile"
      {...fancyBoxProps}
    >
      {tileContent}
    </a>
  );
}
